import CheckValid from "../../Common/CheckValid";
import { billingModeLabel, salesChannelLabel } from "./opsLabels";

// The subscription list's filters, the service query they make, and the CSV of a filtered list.
// Filters are non-personal, so they may sit in the URL; a phone or a frame number never does.

// why (27 Oct 2026, Sachin): the list opens on everyone a plan covers today. A one-time plan paid in
// full is `completed`, so "active" alone missed every one-time buyer. `covered` is this screen's own
// filter: the service is asked for every status and the screen keeps the covered ones.
export const COVERED = "covered";
export const COVERED_STATUSES = ["active", "completed", "cancelling"];
// why (27 Oct 2026, Sachin: "those people never bought"): an unpaid checkout is `pending`, and one that
// lapsed unpaid is `ended`; they are not customers, so the screen shows them apart, after the rest.
export const NOT_BOUGHT_FILTERS = ["pending", "ended"];
export const STATUS_FILTERS = [
  COVERED,
  "active",
  "completed",
  "cancelling",
  "paused",
  "cancelled",
  "refunded",
  "all",
  ...NOT_BOUGHT_FILTERS,
];
export const isCovered = (row) => COVERED_STATUSES.includes(row?.status);
// A plan that ended (or was cancelled) before its first payment: a checkout nobody paid.
export const neverPaid = (row) =>
  ["ended", "cancelled"].includes(row?.status) && !row?.startedAt && (row?.monthsPaid ?? 0) === 0;
export const BILLING_FILTERS = ["monthly", "one_time"];
// The service's sales channels: `app` (every plan bought in the app, and every row from before
// 2 Nov 2026) and `chat_bot` (sold by the support bot in chat).
export const SALES_CHANNEL_FILTERS = ["app", "chat_bot"];
export const DUE_FILTERS = ["7", "14", "30"];
export const SORTS = ["next_due_asc", "started_desc"];

// The tier key check lives in CheckValid (warranty_tier_key); this asks it for the message only.
// "" means the key is fine.
export const tierKeyMessage = (text) => {
  let message = "";
  CheckValid(text, {
    type: "warranty_tier_key",
    error: (m) => {
      message = m;
    },
  });
  return message;
};

export const PAGE_SIZE = 25;
export const CSV_PAGE_SIZE = 100;
// why: the OMS allows 60 reads a minute per staff member and the service 120 a minute for the whole
// OMS, so an export is held to 20 pages, a second apart, and never starves other staff.
export const CSV_ROW_CAP = 2000;
export const CSV_PAGE_PAUSE_MS = 1000;
const RATE_LIMIT_WAIT_MS = 5000;
const RATE_LIMIT_MAX_WAIT_MS = 60000;

export const DEFAULT_FILTERS = {
  status: COVERED,
  needsAttention: false,
  tier: "",
  billingMode: "",
  salesChannel: "",
  dueWithinDays: "",
  sort: "next_due_asc",
};

const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

export const filtersFromParams = (params) => ({
  status: oneOf(params.get("status"), STATUS_FILTERS, DEFAULT_FILTERS.status),
  needsAttention: params.get("attention") === "1",
  tier: params.get("tier") && tierKeyMessage(params.get("tier")) === "" ? params.get("tier") : "",
  billingMode: oneOf(params.get("billing"), BILLING_FILTERS, ""),
  salesChannel: oneOf(params.get("via"), SALES_CHANNEL_FILTERS, ""),
  dueWithinDays: oneOf(params.get("due"), DUE_FILTERS, ""),
  sort: oneOf(params.get("sort"), SORTS, DEFAULT_FILTERS.sort),
});

// Only the values that differ from the defaults, so the plain list has a plain URL.
export const filtersToParams = (filters) => {
  const out = {};
  if (filters.status !== DEFAULT_FILTERS.status) out.status = filters.status;
  if (filters.needsAttention) out.attention = "1";
  if (filters.tier) out.tier = filters.tier;
  if (filters.billingMode) out.billing = filters.billingMode;
  if (filters.salesChannel) out.via = filters.salesChannel;
  if (filters.dueWithinDays) out.due = filters.dueWithinDays;
  if (filters.sort !== DEFAULT_FILTERS.sort) out.sort = filters.sort;
  return out;
};

// The query keys of listSupportSubscriptions. The service refuses unknown keys, so only set ones go.
export const listQuery = (filters, cursor, limit) => {
  const out = { status: filters.status === COVERED ? "all" : filters.status, sort: filters.sort, limit };
  if (filters.needsAttention) out.needsAttention = "true";
  if (filters.tier) out.tier = filters.tier;
  if (filters.billingMode) out.billingMode = filters.billingMode;
  if (filters.salesChannel) out.salesChannel = filters.salesChannel;
  if (filters.dueWithinDays) out.dueWithinDays = filters.dueWithinDays;
  if (cursor) out.cursor = cursor;
  return out;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRateLimited = (result) => result.httpStatus === 429 || result.code === "RATE_LIMITED";

// The service's retry time when it sends one (retryAfterSeconds in the refusal), else 5 seconds.
const rateLimitWaitMs = (result) => {
  const seconds = result.details?.retryAfterSeconds;
  return Number.isInteger(seconds) && seconds > 0
    ? Math.min(seconds * 1000, RATE_LIMIT_MAX_WAIT_MS)
    : RATE_LIMIT_WAIT_MS;
};

// Every page of the filtered list, CSV_PAGE_SIZE at a time and CSV_PAGE_PAUSE_MS apart, up to
// `cap` rows. `fetchList(query)` is listSubscriptions with the token bound. A page refused as too
// many requests is asked for once more after the wait; any other failed page fails the whole
// file, because a partial export would look complete. `wait` is injectable for tests.
export const collectSubscriptions = async (
  fetchList,
  filters,
  { pageSize = CSV_PAGE_SIZE, cap = CSV_ROW_CAP, pauseMs = CSV_PAGE_PAUSE_MS, wait = sleep } = {}
) => {
  const rows = [];
  const seen = new Set();
  let cursor = null;
  // why: "Covered now" keeps only some rows of each page, so the row cap alone may never be met; the
  // pages read are capped too (20 at 100 a page), so an export always ends.
  const maxPages = Math.ceil(cap / pageSize);
  for (let pages = 1; ; pages += 1) {
    const query = listQuery(filters, cursor, pageSize);
    let result = await fetchList(query);
    if (!result.ok && isRateLimited(result)) {
      await wait(rateLimitWaitMs(result));
      result = await fetchList(query);
    }
    if (!result.ok) {
      return { ok: false, message: result.message };
    }
    const items = result.data?.items || [];
    rows.push(...(filters.status === COVERED ? items.filter(isCovered) : items));
    const next = result.data?.nextCursor || null;
    if (rows.length >= cap) {
      return { ok: true, rows: rows.slice(0, cap), capped: rows.length > cap || next !== null };
    }
    if (next === null) {
      return { ok: true, rows, capped: false };
    }
    if (pages >= maxPages) {
      return { ok: true, rows, capped: true };
    }
    // why: a cursor seen before would page forever; stop rather than save a looping file.
    if (seen.has(next)) {
      return {
        ok: false,
        message: "The warranty service sent the same page twice, so the file was not saved. Try again.",
      };
    }
    seen.add(next);
    cursor = next;
    await wait(pauseMs);
  }
};

// why: a spreadsheet runs a cell starting with = + - @ (or a tab or CR before one) as a formula, so
// such a cell is written as text with a leading single quote.
const FORMULA_START = /^[=+\-@\t\r]/;

export const csvCell = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  let text = String(value);
  if (FORMULA_START.test(text)) {
    text = `'${text}`;
  }
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// 692993 -> "6929.93", integer arithmetic only. Every market currency has two minor digits.
export const minorToDecimal = (minor) => {
  if (typeof minor !== "number" || !Number.isFinite(minor)) {
    return "";
  }
  const abs = Math.abs(Math.trunc(minor));
  return `${minor < 0 ? "-" : ""}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
};

const CSV_COLUMNS = [
  ["Plan id", (row) => row.subscriptionId],
  ["Frame number", (row) => row.frameNumber],
  ["Phone (masked)", (row) => row.maskedPhone],
  ["Plan", (row) => row.displayName || row.tier],
  ["Billing", (row) => (row.billingMode ? billingModeLabel(row.billingMode) : null)],
  ["Sold via", (row) => (row.salesChannel ? salesChannelLabel(row.salesChannel) : null)],
  ["Status", (row) => row.status],
  ["Attention", (row) => (Array.isArray(row.attention) ? row.attention.join("; ") : null)],
  ["Months paid", (row) => row.monthsPaid],
  ["Tenure months", (row) => row.tenureMonths],
  ["Instalments left", (row) => row.instalmentsLeft],
  ["Amount left", (row) => minorToDecimal(row.amountLeftMinor)],
  ["Currency", (row) => row.currency],
  ["Next due", (row) => row.nextDueOn],
  ["Grace ends", (row) => row.graceEndsOn],
  ["Started", (row) => row.startedAt],
  ["Coupon in (months)", (row) => row.couponMonthsToGo],
  ["Coupon expected on", (row) => row.couponExpectedOn],
];

// A byte order mark first so a spreadsheet reads the file as UTF-8.
export const subscriptionsCsv = (rows) =>
  "﻿" +
  [CSV_COLUMNS.map(([title]) => title), ...rows.map((row) => CSV_COLUMNS.map(([, read]) => read(row)))]
    .map((cells) => cells.map(csvCell).join(","))
    .join("\r\n") +
  "\r\n";

const pad = (n) => String(n).padStart(2, "0");

export const subscriptionsCsvName = (status, now = new Date()) =>
  `warranty-subscriptions-${oneOf(status, STATUS_FILTERS, "list")}-${now.getFullYear()}-${pad(
    now.getMonth() + 1
  )}-${pad(now.getDate())}.csv`;
